import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RETURN_STATUS } from "@/components/warehouse/Returns";

export const SERIAL_STATUS = { IN_STOCK: ["A magazzino", "bg-sky-50 text-sky-900"], INSTALLED: ["Installato", "bg-emerald-100 text-emerald-900"], REMOVED: ["Scaricato", "bg-zinc-100 text-zinc-700"],
  RETURNED_VAN: RETURN_STATUS.RETURNED_VAN, RETURN_RECEIVED: RETURN_STATUS.RETURN_RECEIVED, REFURBISHING: RETURN_STATUS.REFURBISHING, SCRAPPED: RETURN_STATUS.SCRAPPED, RETURNED_SUPPLIER: RETURN_STATUS.RETURNED_SUPPLIER, MISSING: ["Mancante da inventario", "bg-red-100 text-red-800"] };

const AssignBar = ({ selected, customers, onDone }) => {
  const [cust, setCust] = useState("");
  const save = async () => {
    try {
      const r = await api.post("/warehouse/serials/assign-customer", { serial_ids: selected, customer_id: cust === "none" ? null : cust });
      toast.success(`Committente aggiornata su ${r.data.updated} seriali${r.data.skipped ? ` (${r.data.skipped} esclusi perché installati o fuori dai tuoi magazzini)` : ""}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 border-l-4 border-foreground bg-white px-3 py-2" data-testid="serials-assign-bar">
      <span className="text-sm"><b data-testid="serials-selected-count">{selected.length}</b> seriali selezionati · assegna alla committente</span>
      <Select value={cust || undefined} onValueChange={setCust}>
        <SelectTrigger className="w-64 bg-white" data-testid="serials-assign-customer"><SelectValue placeholder="Seleziona committente" /></SelectTrigger>
        <SelectContent><SelectItem value="none">Nessuna (usabile su tutti gli ordini)</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
      </Select>
      <Button size="sm" disabled={!cust} onClick={save} data-testid="serials-assign-save">Assegna</Button>
    </div>
  );
};

export const SerialsTab = ({ warehouses, refreshKey, canWrite, preset }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [cust, setCust] = useState("all");
  const [scope, setScope] = useState({});
  useEffect(() => {
    if (preset) {
      setStatus("IN_STOCK");
      setCust(preset.customer_id);
      setQ("");
      setScope({ warehouse_id: preset.warehouse_id, item_id: preset.item_id });
    }
  }, [preset]);
  const [sel, setSel] = useState([]);
  const [tick, setTick] = useState(0);
  const { companyId, parties } = useData();
  const customers = parties.filter((p) => p.roles.includes("customer"));
  const [raw, setRows] = useState([]);
  const ids = new Set(warehouses.map((w) => w.id));
  const rows = companyId ? raw.filter((s) => ids.has(s.warehouse_id) || ids.has(s.last_warehouse_id)) : raw;
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/warehouse/serials", { params: { q: q || undefined, status: status === "all" ? undefined : status, customer_id: cust === "all" ? undefined : cust, ...scope } }).then((r) => { setRows(r.data); setSel([]); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, cust, refreshKey, tick, scope]);
  const whName = (id) => warehouses.find((w) => w.id === id)?.name || "—";
  const selectable = rows.filter((s) => s.status !== "INSTALLED");
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div data-testid="serials-tab">
      <div className="mb-3 flex flex-wrap gap-3">
        <Input className="w-72 bg-white font-mono" placeholder="Cerca numero di serie" value={q} onChange={(e) => setQ(e.target.value)} data-testid="serials-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-white" data-testid="serials-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem>{Object.entries(SERIAL_STATUS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={cust} onValueChange={setCust}>
          <SelectTrigger className="w-60 bg-white" data-testid="serials-customer-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le committenti</SelectItem><SelectItem value="none">Senza committente</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        {(scope.warehouse_id || scope.item_id) && (
          <button type="button" className="inline-flex items-center gap-1 border bg-white px-3 text-sm" onClick={() => setScope({})} data-testid="serials-scope-clear">
            {whName(scope.warehouse_id)}{scope.item_id && rows[0] ? ` · ${rows[0].item_code}` : ""} <span className="font-semibold">×</span>
          </button>
        )}
      </div>
      {canWrite && sel.length > 0 && <AssignBar selected={sel} customers={customers} onDone={() => setTick((t) => t + 1)} />}
      {rows.length === 0 ? <Empty text="Nessun seriale trovato" testId="serials-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              {canWrite && <TableHead className="w-8"><input type="checkbox" checked={selectable.length > 0 && sel.length === selectable.length} onChange={(e) => setSel(e.target.checked ? selectable.map((s) => s.id) : [])} data-testid="serials-select-all" /></TableHead>}
              <TableHead>Seriale</TableHead><TableHead>Articolo</TableHead><TableHead>Committente</TableHead><TableHead>Stato</TableHead><TableHead>Posizione</TableHead><TableHead>Ordine</TableHead><TableHead>Aggiornato</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => {
                const [label, cls] = SERIAL_STATUS[s.status] || [s.status, ""];
                return (
                  <TableRow key={s.id} data-testid={`serial-row-${s.serial}`}>
                    {canWrite && <TableCell>{s.status !== "INSTALLED" && <input type="checkbox" checked={sel.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`serial-select-${s.serial}`} />}</TableCell>}
                    <TableCell className="font-mono text-sm font-semibold">{s.serial}</TableCell>
                    <TableCell className="text-sm"><span className="font-mono text-xs">{s.item_code}</span> {s.item_name}</TableCell>
                    <TableCell className="text-sm" data-testid={`serial-customer-${s.serial}`}>{s.customer_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><span className={`px-1.5 py-0.5 text-[11px] font-semibold uppercase ${cls}`}>{label}</span></TableCell>
                    <TableCell className="text-sm">{s.warehouse_id ? whName(s.warehouse_id) : s.status === "INSTALLED" ? "Presso cliente" : `ex ${whName(s.last_warehouse_id)}`}</TableCell>
                    <TableCell>{s.work_order_id ? <Link className="font-mono text-xs font-semibold underline" to={`/ordini/${s.work_order_id}`}>{s.external_order_id}</Link> : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtDateTime(s.updated_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
